import { CopySimpleIcon } from '@phosphor-icons/react';
import {
  Button,
  Cell,
  Column,
  Avatar,
  EmptyState,
  Pagination,
  Row,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { flagImages } from '../legacy-bridge';
import { canMerge, skuOptions, titleOptions } from '../lib/duplicates';
import type { DuplicateGroup, MergeChoice } from '../lib/types';

export type DuplicatesTableProps = {
  groups: DuplicateGroup[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalData: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  choices: Record<string, MergeChoice>;
  onChoose: (
    group: DuplicateGroup,
    field: 'p_sku' | 'p_title',
    value: string,
  ) => void;
  onMerge: (group: DuplicateGroup) => void;
  /** The group being merged right now. */
  mergingId?: string | null;
  /** Region names by id, for the flag. */
  regionNames: Record<string, string>;
  isReadOnly?: boolean;
  isActionsHidden?: boolean;
};

const LOADING_ROWS = 7;
const COLUMN_COUNT = 4;

/* A field the listings agree on reads as text; otherwise one is picked. */
function FieldChoice({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value?: string;
  onChange: (value: string) => void;
}) {
  if (options.length <= 1) {
    return (
      <span className="sk:block sk:truncate sk:text-sm sk:text-fg">
        {value ?? options[0] ?? '—'}
      </span>
    );
  }

  return (
    <Select
      aria-label={label}
      placeholder={label}
      value={value}
      onChange={onChange}
      options={options.map(option => ({ value: option, label: option }))}
      className="sk:w-full"
    />
  );
}

/** Catalog items whose listings disagree, each merged on one title and SKU. */
export function DuplicatesTable({
  groups,
  loading,
  page,
  pageSize,
  totalData,
  onPageChange,
  onPageSizeChange,
  choices,
  onChoose,
  onMerge,
  mergingId,
  regionNames,
  isReadOnly,
  isActionsHidden,
}: DuplicatesTableProps) {
  return (
    <TableContainer className="sk:overflow-visible sk:rounded-card sk:bg-surface">
      <Table aria-label="Duplicate listings" className="sk:table-fixed">
        <TableHeader>
          <Column id="title" isRowHeader className="sk:w-[44%]">
            Product name
          </Column>
          <Column id="sku" className="sk:w-[30%]">
            Correct SKU for this product
          </Column>
          <Column id="region" className="sk:w-[12%]">
            Region
          </Column>
          <Column id="actions" aria-label="Actions" className="sk:w-[14%]">
            {''}
          </Column>
        </TableHeader>
        <TableBody
          renderEmptyState={() => (
            <EmptyState
              icon={<CopySimpleIcon size={20} />}
              title="No duplicate listings"
              description="Every catalog item's listings agree on its title and SKU."
            />
          )}
        >
          {loading
            ? Array.from({ length: LOADING_ROWS }, (_, row) => (
                <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
                  <Cell colSpan={COLUMN_COUNT}>
                    <div className="sk:h-10 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
                  </Cell>
                </Row>
              ))
            : groups.map(group => {
                const choice = choices[group._id];
                const region = group.marketplace_region_id
                  ? regionNames[group.marketplace_region_id]
                  : undefined;
                const flag = region
                  ? flagImages[region.toUpperCase()]
                  : undefined;

                return (
                  <Row key={group._id} id={group._id}>
                    <Cell>
                      <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-3">
                        <Avatar
                          src={group.p_images?.display_image}
                          name={choice?.p_title ?? titleOptions(group)[0]}
                          shape="square"
                          size="md"
                        />
                        <div className="sk:min-w-0 sk:flex-1">
                          <FieldChoice
                            label="Product name"
                            options={titleOptions(group)}
                            value={choice?.p_title}
                            onChange={value =>
                              onChoose(group, 'p_title', value)
                            }
                          />
                        </div>
                      </div>
                    </Cell>
                    <Cell>
                      <FieldChoice
                        label="Product SKU"
                        options={skuOptions(group)}
                        value={choice?.p_sku}
                        onChange={value => onChoose(group, 'p_sku', value)}
                      />
                    </Cell>
                    <Cell>
                      {region ? (
                        <span className="sk:inline-flex sk:items-center sk:gap-1.5 sk:text-dense sk:text-fg-secondary">
                          {flag ? (
                            <img
                              src={flag}
                              alt=""
                              className="sk:h-3 sk:w-4 sk:rounded-xs sk:object-cover"
                            />
                          ) : null}
                          {region}
                        </span>
                      ) : (
                        <span className="sk:text-fg-tertiary">—</span>
                      )}
                    </Cell>
                    <Cell>
                      {isActionsHidden ? null : (
                        <div className="sk:flex sk:justify-end">
                          <Button
                            variant="primary"
                            size="sm"
                            isDisabled={
                              !canMerge(choice) || isReadOnly || !!mergingId
                            }
                            onPress={() => onMerge(group)}
                          >
                            {mergingId === group._id ? 'Merging…' : 'Merge'}
                          </Button>
                        </div>
                      )}
                    </Cell>
                  </Row>
                );
              })}
        </TableBody>
      </Table>
      {totalData > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalData}
          onPageChange={nextPage => {
            onPageChange(nextPage);
            window.scrollTo({ top: 0 });
          }}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </TableContainer>
  );
}
